/**
 * @module components/marketing/FeaturedOn
 * @description "Featured on" press/product-directory logo strip.
 *
 * @extracted-from client/src/App.js (lines ~1734-1821)
 */

import React from 'react';
import sentientLogo from '../../assets/sentient-new-logo.avif';
import theDailyBiteLogo from '../../assets/thedailybite-transparent.png';
import productHuntLogo from '../../assets/producthunt-transparent.png';
import aiPureLogo from '../../assets/aipure-transparent.png';
import steemHuntLogo from '../../assets/steemhunt.png';
import { Eyebrow } from '../ui';

/**
 * @param {Object} [props]
 * @param {Object} [props.logos] - Optional override for the logo assets; defaults to the bundled brand logos.
 * @param {string} [props.logos.sentientLogo]
 * @param {string} [props.logos.theDailyBiteLogo]
 * @param {string} [props.logos.productHuntLogo]
 * @param {string} [props.logos.aiPureLogo]
 * @param {string} [props.logos.steemHuntLogo]
 */
const FeaturedOn = ({ logos = {} }) => {
  const {
    sentientLogo: sentient = sentientLogo,
    theDailyBiteLogo: theDailyBite = theDailyBiteLogo,
    productHuntLogo: productHunt = productHuntLogo,
    aiPureLogo: aiPure = aiPureLogo,
    steemHuntLogo: steemHunt = steemHuntLogo,
  } = logos;

  return (
    <section className="mt-16 md:mt-20 text-center" aria-labelledby="featured-on-heading">
      <Eyebrow id="featured-on-heading" className="mb-3">
        Featured on
      </Eyebrow>
      <p className="text-sm text-critic-text-secondary font-normal max-w-md mx-auto mb-8 px-2">
        Press and product directories that have featured CriticAI.
      </p>
      <div className="rounded-2xl border border-white/[0.08] bg-critic-bg-card/25 backdrop-blur-xl px-5 py-10 md:px-12 md:py-12">
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-10 md:gap-x-14">
          <a
            href="https://sentient.xyz"
            target="_blank"
            rel="noopener noreferrer"
            className="group transition-transform duration-critic ease-critic-out hover:scale-[1.03]"
          >
            <div className="relative">
              <img
                src={sentient}
                alt="Sentient"
                className="h-5 md:h-7 w-auto opacity-60 group-hover:opacity-100 transition-opacity duration-critic"
                style={{ mixBlendMode: 'multiply', backgroundColor: 'transparent', filter: 'brightness(0) invert(1)' }}
              />
            </div>
          </a>
          <a
            href="https://x.com/TheDailyBiteCo/status/1952508480918540779"
            target="_blank"
            rel="noopener noreferrer"
            className="group transition-transform duration-critic ease-critic-out hover:scale-[1.03]"
          >
            <div className="relative">
              <img
                src={theDailyBite}
                alt="The Daily Bite"
                className="h-8 md:h-12 w-auto opacity-60 group-hover:opacity-100 transition-opacity duration-critic filter grayscale group-hover:grayscale-0"
              />
            </div>
          </a>
          <a
            href="https://www.producthunt.com/products/criticai"
            target="_blank"
            rel="noopener noreferrer"
            className="group transition-transform duration-critic ease-critic-out hover:scale-[1.03]"
          >
            <div className="relative">
              <img
                src={productHunt}
                alt="Product Hunt"
                className="h-8 md:h-12 w-auto opacity-60 group-hover:opacity-100 transition-opacity duration-critic filter grayscale group-hover:grayscale-0"
              />
            </div>
          </a>
          <a
            href="https://aipure.ai/products/criticai"
            target="_blank"
            rel="noopener noreferrer"
            className="group transition-transform duration-critic ease-critic-out hover:scale-[1.03]"
          >
            <div className="relative">
              <img
                src={aiPure}
                alt="AI Pure"
                className="h-8 md:h-12 w-auto opacity-60 group-hover:opacity-100 transition-opacity duration-critic filter grayscale group-hover:grayscale-0"
              />
            </div>
          </a>
          <a
            href="https://steemhunt.com/@eoyf9k/criticai-ai-powered-music-analysis-for-a-and-r-teams-artists-etc"
            target="_blank"
            rel="noopener noreferrer"
            className="group transition-transform duration-critic ease-critic-out hover:scale-[1.03]"
          >
            <div className="relative">
              <img
                src={steemHunt}
                alt="SteemHunt"
                className="h-8 md:h-12 w-auto opacity-60 group-hover:opacity-100 transition-opacity duration-critic filter grayscale group-hover:grayscale-0"
              />
            </div>
          </a>
        </div>
      </div>
    </section>
  );
};

export default FeaturedOn;
